import React from 'react';
import { ArrowRight, ShoppingBag } from 'lucide-react';
import './ProductAd.css';

// Fallback search URL when BidTurns doesn't return a real product_url.
function buildFallbackUrl({ productName, vertical, cats }) {
  const q = productName || cats?.[0] || vertical;
  if (!q) return null;
  return `https://www.google.com/search?tbm=shop&q=${encodeURIComponent(q)}`;
}

/**
 * Sponsored recommendation card. Renders ONLY when BidTurns has matched
 * an actual product (`ad.product_name`). Intent-only signal (verticals
 * + categories without a brand match) is suppressed — those generic
 * "Related products for you" cards are noise.
 *
 * CSS class names use the neutral `pj-recos-` prefix so EasyList / uBlock
 * / Brave Shields don't pattern-match "ad" / "sponsored" and hide it.
 */
function ProductAd({ ad, intent }) {
  // Strict gate: only a real product match shows the card.
  if (!ad || !ad.product_name) return null;

  const productName = ad.product_name;
  const productScore = typeof ad.product_score === 'number' ? ad.product_score : null;
  const productUrl = ad.product_url || null;
  const productImage = ad.product_image_url || null;
  const cats = intent?.micro_categories || [];
  const vertical = intent?.parent_vertical || '';

  // Compact meta line: "Vertical · Cat1 · Cat2 · 95% match"
  const metaParts = [];
  if (vertical) metaParts.push(capitalize(vertical));
  cats.slice(0, 2).forEach((c) => metaParts.push(capitalize(c)));
  if (productScore !== null) metaParts.push(`${productScore}% match`);

  const href = productUrl || buildFallbackUrl({ productName, vertical, cats });

  return (
    <div className="pj-recos">
      <div className="pj-recos-row">
        <div className="pj-recos-icon">
          {productImage ? (
            <img src={productImage} alt="" />
          ) : (
            <ShoppingBag size={16} />
          )}
        </div>
        <div className="pj-recos-text">
          <div className="pj-recos-head">
            <span className="pj-recos-name">{productName}</span>
            <span className="pj-recos-tag">Sponsored</span>
          </div>
          {metaParts.length > 0 && (
            <div className="pj-recos-meta">{metaParts.join(' · ')}</div>
          )}
        </div>
      </div>

      {href && (
        <a
          className="pj-recos-cta"
          href={href}
          target="_blank"
          rel="noopener noreferrer"
        >
          Learn more <ArrowRight size={13} />
        </a>
      )}
    </div>
  );
}

function capitalize(s) {
  if (!s) return s;
  return s.charAt(0).toUpperCase() + s.slice(1);
}

export default ProductAd;
